import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Expense } from '#/graphql/types';
import type { FormAction, FormItem, FormRefType } from '#/common/components/Form';
import Form from '#/common/components/Form';
import useToast from '#/common/hooks/useToast';
import { useNavigate } from 'react-router-dom';
import AttachmentsUpload from '#/common/components/AttachmentUpload';
import allFields from '../../utils/fields';
import { getCleanFormErrors } from '#/common/utils/customRelatedItemsValidation';
import { get } from 'lodash';
import ScanSelector from '#/common/components/ScanSelector';
import useOcrProcessing, { OcrFieldResult } from '#/common/hooks/useOcrProcessing';
import useUploadScannedFile from '#/common/hooks/useUploadScannedFile';
import dxForm, { FieldDataChangedEvent } from 'devextreme/ui/form';
import { setEditorOptions } from '#/common/components/Form/formItemOption';
import { extractFieldNames } from '#/common/utils/extractFieldNames';
import useExpenseDelete from '../../hooks/useExpenseDelete';
import { InitializedEventInfo } from 'devextreme/events';

// OCR field constants for expense receipts
const OCR_COMPANY_NAME = 'company name';
const OCR_DATE = 'date';
const OCR_TOTAL_AMOUNT = 'total amount';
const OCR_DESCRIPTION = 'description';

export interface ExpenseFormProps {
  expense: Expense;
  title: string;
  fields: FormItem<Expense>[];
  submit?: (
    expense: Expense,
  ) => Promise<{ data?: Expense; errors?: string[] } | undefined> | undefined;
}

const ExpenseForm: React.FC<ExpenseFormProps> = ({ expense, title, fields, submit }) => {
  const { alert, confirm } = useToast();
  const navigate = useNavigate();
  const formRef = useRef<FormRefType | null>(null);
  const uploadScannedFile = useUploadScannedFile();
  const { getOcrSuccessMessage } = useOcrProcessing();
  const { deleteExpense } = useExpenseDelete();

  // State for ScanSelector
  const id = get(expense, 'id', undefined);
  const [showExpenseScanSelector, setShowExpenseScanSelector] = useState(!id); // Show for new expenses
  const [tenantId, setTenantId] = useState<string | undefined>(get(expense, 'tenantId'));
  const [scannedFile, setScannedFile] = useState<File | null>(null);

  // Reset form field options when switching to a new expense
  useEffect(() => {
    if (!id && formRef.current?.instance) {
      // Reset readonly state for all fields when creating new expense
      const allFieldNames = extractFieldNames(fields, ['tenantId']);
      allFieldNames.forEach((fieldName) => {
        setEditorOptions(formRef.current!.instance(), fieldName, { readOnly: false });
      });
    }
  }, [id, fields]);

  const statusIsExpended = expense.status === 'expended';

  const validateForm = (e: any) => {
    const validation = e.form?.validate();
    if (
      !validation?.isValid &&
      e.record &&
      getCleanFormErrors(validation, allFields, e.record).length
    ) {
      void alert('error', `${title} is not valid, check field entries.`);
      return false;
    }
    return true;
  };

  const checkStatusChange = (e: any) => {
    return expense.status !== 'expended' && e.record.status === 'expended';
  };

  const confirmProceed = async () => {
    return await confirm(
      'warning',
      "Warning: Marking this expense as 'Expended' will finalize it and add its cost to the referenced Work Order. You will no longer be able to edit or delete this expense. Do you want to proceed?",
    );
  };

  const handleSubmissionResult = async (result: any, e: any) => {
    if (result?.errors) {
      void alert('error', result.errors.join(`;\n`));
      return true;
    }
    if (!id && result?.data?.id) {
      if (scannedFile) {
        try {
          await uploadScannedFile('expenses', result.data.id, scannedFile);
          setScannedFile(null);
        } catch {
          navigate(`/expenses/${result.data.id}`, { replace: true });
          return true;
        }
      }
      navigate(`/expenses/${result.data.id}`, { replace: true });
      void alert('success', 'Changes saved!');
      return true;
    }
    return false;
  };
  const makeFieldsReadOnly = (form: any) => {
    const allFieldNames = extractFieldNames(fields, ['tenantId']);
    allFieldNames.forEach((fieldName) => {
      setEditorOptions(form, fieldName, { readOnly: true });
    });
  };

  const deleteItem = useCallback(
    (id: string) => {
      if (!id) {
        return undefined;
      }
      return deleteExpense(id);
    },
    [id, deleteExpense],
  );

  const actions = useMemo(() => {
    const result: FormAction<Expense>[] = [];

    if (deleteItem && id) {
      result.push({
        label: 'Delete',
        icon: 'trash',
        type: 'danger',
        disabled: statusIsExpended,
        onClick: async (e) => {
          if (!e.record) {
            return;
          }
          const sure = await confirm(
            'error',
            `Are you sure you want to delete expense: ${e.record.displayNumber}?`,
          );

          if (!sure) {
            return;
          }

          e.setIsLoading(true);
          await deleteItem(e.record.id);
          e.setIsLoading(false);

          navigate('/expenses');
        },
      });
    }

    if (submit) {
      result.push({
        label: 'Submit',
        type: 'submit',
        disabled: statusIsExpended,
        onClick: async (e) => {
          if (!e.record) {
            return;
          }

          if (!validateForm(e)) {
            return;
          }

          const isChangingToExpended = checkStatusChange(e);
          if (isChangingToExpended && !(await confirmProceed())) {
            return;
          }

          try {
            e.setIsLoading(true);
            const result = await submit(e.record);

            if (await handleSubmissionResult(result, e)) {
              return;
            }

            if (isChangingToExpended && e.form) {
              makeFieldsReadOnly(e.form);
            }

            e.clearDirty();
            void alert('success', 'Changes saved!');
            formRef.current?.repaint();
          } catch (error: any) {
            console.log('Error', error);
            void alert('error', 'Missing or invalid data!');
          } finally {
            e.setIsLoading(false);
          }
        },
      });
    }

    return result;
  }, [
    alert,
    confirm,
    deleteExpense,
    navigate,
    submit,
    expense,
    scannedFile,
    statusIsExpended,
    fields,
    id,
    tenantId,
    uploadScannedFile,
  ]);

  // Handle expense scan selection
  const fieldsToScan = [OCR_COMPANY_NAME, OCR_DATE, OCR_TOTAL_AMOUNT, OCR_DESCRIPTION];
  const handleExpenseScanSelect = useCallback(
    (file: File | null, result: OcrFieldResult, error?: string) => {
      if (!formRef.current) return;

      if (error) {
        console.error('Error processing image:', error);
        void alert('error', `Error processing image: ${error}`);
        return;
      }

      if (file) {
        setScannedFile(file);
        console.log(formRef.current);
      }

      const populatedFields: string[] = [];

      if (result[OCR_COMPANY_NAME]) {
        formRef.current?.updateData(
          'title',
          `${result[OCR_COMPANY_NAME]} ${result[OCR_DATE] || ''}`.trim(),
        );
        populatedFields.push(allFields.title.label);
      }
      if (result[OCR_TOTAL_AMOUNT]) {
        // Extract numeric value from amount (e.g., "$38.37" -> 38.37)
        const amount = Number.parseFloat(result[OCR_TOTAL_AMOUNT].replaceAll(/[^0-9.]/g, ''));
        formRef.current?.updateData('amount', amount);
        populatedFields.push(allFields.amount.label);
      }
      if (result[OCR_DESCRIPTION]) {
        formRef.current?.updateData('description', result[OCR_DESCRIPTION]);
        populatedFields.push(allFields.description.label);
      }

      // Repaint the form to show the updated values
      formRef.current?.repaint();
      setShowExpenseScanSelector(false);

      // Show success alert with populated fields
      const successMessage = getOcrSuccessMessage(populatedFields);
      if (successMessage) {
        void alert('success', successMessage);
      } else {
        void alert('warning', "Couldn't read the receipt. You can edit fields manually.");
      }
    },
    [],
  );

  const onCloseExpenseScanSelector = useCallback(() => {
    setShowExpenseScanSelector(false);
  }, []);

  const onFieldDataChanged = useCallback((e: FieldDataChangedEvent) => {
    const { component: form, dataField: field } = e;
    const formData = form.option('formData');
    if (field === 'tenantId') {
      setTenantId(formData.tenantId);
    }
  }, []);

  return (
    <>
      {/* Show ScanSelector for new expenses after tenant is selected */}
      {showExpenseScanSelector && tenantId && (
        <ScanSelector
          onOcrResult={handleExpenseScanSelect}
          onClose={onCloseExpenseScanSelector}
          modalChoiceText="Scan Receipt"
          fieldsToScan={fieldsToScan}
        />
      )}

      <Form
        key={expense.id || 'new'}
        ref={formRef}
        readonly={statusIsExpended}
        fields={fields}
        record={expense}
        actions={actions}
        supportTenant
        onFieldDataChanged={onFieldDataChanged}
      />
      {expense.id && (
        <AttachmentsUpload
          relatedTable="expenses"
          relatedItem={expense.id}
          disableUpload={statusIsExpended}
          tenantId={expense.tenantId}
        />
      )}
    </>
  );
};

export default React.memo(ExpenseForm);
